import React from 'react';
import Icon, { IconName } from '../Icon';
import { useTranslation } from '../../i18n';
import './PriorityIcon.css';

export type Priority = 'high' | 'medium' | 'low';

export const isPriority = (value: unknown): value is Priority =>
  value === 'high' || value === 'medium' || value === 'low';

export const PRIORITY_SHAPES: Record<Priority, IconName> = {
  high: 'priorityHigh',
  medium: 'priorityMedium',
  low: 'priorityLow',
};

interface PriorityIconProps {
  priority: Priority;
  /** Hide the label from assistive tech when visible text next to the icon already says it. */
  decorative?: boolean;
  /** false inside a badge that already sets the priority colour */
  colored?: boolean;
  className?: string;
}

/** Priority shown by shape as well as colour (UX-33): ⇈ high, = medium, ⌄ low. */
const PriorityIcon: React.FC<PriorityIconProps> = ({ priority, decorative = false, colored = true, className = '' }) => {
  const { t } = useTranslation();
  const label = t(`goals.priorities.${priority}`);
  return (
    <span
      className={`priority-shape ${colored ? `priority-shape--${priority}` : ''} ${className}`.replace(/\s+/g, ' ').trim()}
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label, title: label })}
    >
      <Icon name={PRIORITY_SHAPES[priority]} />
    </span>
  );
};

export default PriorityIcon;
